import { logError } from '@ecomm/error-handling'
import type { Locale } from '@ecomm/utils/locale/types'
import type { i18n } from 'i18next'
import type i18next from 'i18next'
import type { InitOptions } from 'i18next'
import { createInstance } from 'i18next'
import type { ReactElement, ReactNode } from 'react'
import { I18nextProvider, initReactI18next } from 'react-i18next'

// UK
import enGBCommon from './locales/en-GB/common.json'
import enGBContact from './locales/en-GB/contact.json'
import crimeInTheUk from './locales/en-GB/crime-in-the-uk.json'
import enGBMaps from './locales/en-GB/maps.json'
import enGBShop from './locales/en-GB/shop.json'
import enGBShopSMB from './locales/en-GB/shop.smb.json'
import enGBSystemAdvisor from './locales/en-GB/system-advisor.json'

// US
import enUSCommon from './locales/en-US/common.json'
import enUSContact from './locales/en-US/contact.json'
import enUSHeader from './locales/en-US/header.json'
import enUSMaps from './locales/en-US/maps.json'
import enUSShop from './locales/en-US/shop.json'
import enUSShopSMB from './locales/en-US/shop.smb.json'
import enUSSystemAdvisor from './locales/en-US/system-advisor.json'

// we keep an empty translation namespace in order to force users to use a namespace
// if we don't do this, i18next will default to the first namespace we provide and that can cause some confusion
// by adding an empty translation namespace, users will get a lint error if they try to access a key without a namespace
const resources = {
  'en-US': {
    translation: {},
    contact: enUSContact,
    common: enUSCommon,
    'shop.smb': enUSShopSMB,
    shop: enUSShop,
    header: enUSHeader,
    maps: enUSMaps,
    'system-advisor': enUSSystemAdvisor,
    'crime-in-the-uk': crimeInTheUk
  },
  'en-GB': {
    translation: {},
    contact: enGBContact,
    common: enGBCommon,
    'shop.smb': enGBShopSMB,
    shop: enGBShop,
    header: enUSHeader, // Using the same header as US for now, can be changed later
    maps: enGBMaps,
    'system-advisor': enGBSystemAdvisor,
    'crime-in-the-uk': crimeInTheUk
  }
} as const

const initOptions: InitOptions = {
  fallbackLng: 'en-US',
  debug: process.env['NODE_ENV'] === 'development',
  // I18n will default to the first namespace listed here if a defaultNs is not specified.
  // If we don't specify an ns array nor a defaultNs, it will default to "translation"
  ns: ['translation', 'contact', 'common', 'shop.smb', 'shop'],
  interpolation: {
    escapeValue: false
  },
  supportedLngs: ['en-US', 'en-GB'],
  initAsync: false
}

/*
 * Creates a new i18n instance that we can pass down to I18NProvider
 */
const createI18nInstance = () => createInstance()

/*
 * Initializes an existing i18n instance with the provided locale and our default config options
 */
const initializeI18nInstance = (i18next: i18n, lng: Locale) =>
  i18next.use(initReactI18next).init({
    ...initOptions,
    lng,
    resources: {
      [lng]: resources[lng]
    }
  })

/*
 * Creates and initializes a new i18n instance with the provided locale
 */
const createAndInitializeI18n = (lng: Locale) => {
  const i18next = createI18nInstance()

  // the initialization is async and we don't need to wait for it to finish
  initializeI18nInstance(i18next, lng)
    .then(() => i18next)
    .catch((e: unknown) => {
      return e instanceof Error
        ? logError(e, {
            errorCategory: 'i18n'
          })
        : logError(new Error('Failed to initialize i18n'), {
            errorCategory: 'i18n',
            error: JSON.stringify(e)
          })
    })

  return i18next
}

export { useTranslation } from 'react-i18next'

declare module 'i18next' {
  interface CustomTypeOptions {
    readonly resources:
      | (typeof resources)['en-US']
      | (typeof resources)['en-GB']
  }
}

type I18nProviderProps = {
  readonly children: ReactNode
  readonly i18nInstance: typeof i18next
}

const I18nProvider = ({
  children,
  i18nInstance
}: I18nProviderProps): ReactElement => {
  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>
}

export {
  createI18nInstance,
  initializeI18nInstance,
  createAndInitializeI18n,
  resources,
  I18nProvider
}
